import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';

import { PictoMagicLink, PictoMoto, PictoVoirDetail } from '@/assets/icons-april-fr/pictos';
// Décommenter avec les usages quand les apps stores seront prêts (TypeScript refuse un import inutilisé) :
// import AppDownloadBanner from '@/components/shared/app-download-banner';
import Text from '@/components/forms/text';
import SmallSpinner from '@/components/shared/spinners/small-spinner';
import { APP_NAME, AUTH_GRAVITEE_ENABLED, HOST_MB } from '@/lib/constants';
import { useLogin, type LoginValues } from '@/hooks/auth/use-auth';
import { useWhitelabelByHost } from '@/hooks/api/use-whitelabel';
import useIsPWA from '@/hooks/use-is-pwa';
import usePageTitle from '@/hooks/use-page-title';
import { useUiStore } from '@/stores/ui-store';
import { isNativePlatform } from '@/lib/capacitor';
import { initiateGraviteeLogin } from '@/lib/auth/gravitee';

const initialValues: LoginValues = { username: '', password: '' };

const Connection = () => {
    const login = useLogin();
    const loading = login.isPending;
    const erreur = login.isError;
    const isLogin = useUiStore((state) => state.login);
    const name = useWhitelabelByHost().data?.name;
    const isPWA = useIsPWA();
    usePageTitle('Connexion');

    const { register, handleSubmit, watch } = useForm<LoginValues>({ defaultValues: initialValues });

    const [showPassword, setShowPassword] = useState(false);
    const togglePassword = () => setShowPassword((s) => !s);

    const [faqOpen, setFaqOpen] = useState(false);

    if (isLogin){
        const urlOrigin = localStorage.getItem(`url-${APP_NAME}`)
        localStorage.removeItem(`url-${APP_NAME}`)

        // Validation anti-open redirect : seules les URLs de même origine sont autorisées
        try {
            const url = new URL(urlOrigin ?? '')
            if (url.origin === window.location.origin) {
                window.location.href = url.href
            } else {
                window.location.href = '/'
            }
        } catch {
            window.location.href = '/'
        }
    }

    // En mode application native (Capacitor), le MFA/Gravitee est bloqué (la redirection
    // OAuth ne fonctionne pas dans la WebView) : on force le login classique et on masque
    // la section mfa-prelogin ainsi que la popin Gravitee de saisie du numéro de téléphone.
    if (AUTH_GRAVITEE_ENABLED && !isNativePlatform()) {
        return (
            <div>
                <h1 className={'h2 mb-6'}>
                    {HOST_MB === 43397 ? (
                        <>
                            Bienvenue sur votre <span className={'text-[oklch(from_var(--color-primary)_calc(l*0.95)_c_h)]'}>espace {name}</span>
                        </>
                    ) : (
                        <>
                            Bienvenue sur votre <span className={'text-[oklch(from_var(--color-primary)_calc(l*0.95)_c_h)]'}>espace assuré 2-roues</span>
                        </>
                    )}
                </h1>
                <div className="grid gap-2">
                    <button
                        type="button"
                        className="inline-flex items-center justify-center gap-2 px-5 py-2.5 text-[0.875rem]/5 font-medium rounded-md transition-all ease-[ease] cursor-pointer no-underline disabled:opacity-50 disabled:pointer-events-none bg-primary text-primary-foreground border-none hover:bg-primary-hover"
                        onClick={() => {
                            const forceLogin = sessionStorage.getItem('gravitee_force_login') === 'true';
                            sessionStorage.removeItem('gravitee_force_login');
                            void initiateGraviteeLogin({ forceLogin });
                        }}
                    >
                        Se connecter
                    </button>
                </div>

                <section className="mfa-prelogin" aria-label="Sécurisation de votre connexion">
                        <div className="mfa-head">
                            <div className="mfa-shield">
                                <svg
                                    width="22"
                                    height="22"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    aria-hidden="true"
                                >
                                    <path d="M12 2l8 3v6c0 5-3.4 8.5-8 11-4.6-2.5-8-6-8-11V5l8-3z" fill="#3EAAC4" />
                                    <path
                                        d="M8.5 12l2.3 2.3L15.5 9.5"
                                        stroke="#fff"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </div>
                            <div>
                                <span className="mfa-badge">Nouveau</span>
                                <div className="mfa-title">Votre connexion est encore mieux protégée</div>
                            </div>
                        </div>

                        <p className="mfa-intro">
                            Pour protéger votre compte et vos données personnelles, nous ajoutons une vérification par
                            SMS au moment de la connexion. C'est simple, rapide et sans aucune installation.
                        </p>

                        <div className="mfa-etapes">
                            <div className="mfa-etape">
                                <div className="mfa-ico">1</div>
                                <div className="mfa-etape-txt">
                                    <b>Vous vous connectez comme d'habitude</b>
                                    <span>Avec votre identifiant et votre mot de passe habituels.</span>
                                </div>
                            </div>
                            <div className="mfa-etape">
                                <div className="mfa-ico">2</div>
                                <div className="mfa-etape-txt">
                                    <b>Vous recevez un code par SMS</b>
                                    <span>
                                        Un code à usage unique est envoyé sur votre téléphone, en quelques secondes.
                                    </span>
                                </div>
                            </div>
                            <div className="mfa-etape">
                                <div className="mfa-ico">3</div>
                                <div className="mfa-etape-txt">
                                    <b>Vous saisissez le code</b>
                                    <span>Et vous accédez à votre espace en toute sécurité.</span>
                                </div>
                            </div>
                        </div>

                        <div className="mfa-option">
                            <svg
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                aria-hidden="true"
                            >
                                <rect x="3" y="5" width="18" height="16" rx="2" stroke="#639E30" strokeWidth="1.8" />
                                <path
                                    d="M3 9h18M8 3v4M16 3v4"
                                    stroke="#639E30"
                                    strokeWidth="1.8"
                                    strokeLinecap="round"
                                />
                                <path
                                    d="M9 15l2 2 4-4"
                                    stroke="#639E30"
                                    strokeWidth="1.8"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                            <p>
                                Vous pouvez choisir de <b>faire confiance à cet appareil pendant 10 jours</b> : sur
                                cette période, plus besoin de saisir de code à chaque connexion depuis ce navigateur.
                            </p>
                        </div>

                        <div className="mfa-reassur">
                            <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                aria-hidden="true"
                            >
                                <rect x="5" y="11" width="14" height="9" rx="2" stroke="#5C6B76" strokeWidth="1.8" />
                                <path d="M8 11V8a4 4 0 018 0v3" stroke="#5C6B76" strokeWidth="1.8" />
                            </svg>
                            Votre numéro reste confidentiel et ne sert qu'à sécuriser votre connexion.
                        </div>

                        <div className={`mfa-faq ${faqOpen ? 'open' : ''}`}>
                            <button
                                type="button"
                                className="mfa-faq-summary"
                                onClick={() => setFaqOpen((o) => !o)}
                                aria-expanded={faqOpen}
                            >
                                <svg
                                    className="mfa-chev"
                                    width="10"
                                    height="10"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    aria-hidden="true"
                                >
                                    <path
                                        d="M8 5l8 7-8 7"
                                        stroke="#3EAAC4"
                                        strokeWidth="2.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                                Quelques questions fréquentes
                            </button>
                            <div className="mfa-faq-wrap">
                                <div className="mfa-faq-inner">
                                    <div className="mfa-faq-item">
                                        <b>Je n'ai pas reçu mon code, que faire ?</b>
                                        <span>
                                            Patientez quelques instants, puis demandez un nouvel envoi depuis la page de
                                            saisie du code.
                                        </span>
                                    </div>
                                    <div className="mfa-faq-item">
                                        <b>Sur quel numéro vais-je recevoir le SMS ?</b>
                                        <span>
                                            Sur le numéro de mobile renseigné dans votre dossier client. Vous pourrez le
                                            vérifier après connexion.
                                        </span>
                                    </div>
                                    <div className="mfa-faq-item">
                                        <b>Devrai-je le faire à chaque fois ?</b>
                                        <span>
                                            Non, si vous choisissez de faire confiance à votre appareil : vous serez
                                            tranquille pendant 10 jours.
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                </section>

                {/* Badges stores commentés en attendant la publication des apps. Décommenter quand prêt. */}
                {/* {!isPWA && HOST_MB === 43397 && <AppDownloadBanner variant="compact" />} */}
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit(values => login.mutate(values))}>
            <h1 className={'mb-6 font-bold'} style={{fontSize: '1.75rem'}}>
                <PictoMoto style={{width: 42, height: 42, verticalAlign: 'middle', display: 'inline-block', marginRight: 8}} />
                {HOST_MB === 43397
                    ? <>Bienvenue sur votre <span className={'text-[oklch(from_var(--color-primary)_calc(l*0.95)_c_h)]'}>Espace {name}</span></>
                    : <>Bienvenue sur votre <span className={'text-[oklch(from_var(--color-primary)_calc(l*0.95)_c_h)]'}>espace assuré 2-roues</span></>
                }
            </h1>

            <div className={'mb-6'}>
                <Text
                    type={'text'}
                    autoComplete="email"
                    {...register('username')}
                    value={watch('username')}
                    required
                    label={"Adresse email"}
                />

                <Link to={'/login/oubli-email'} className={'inline-block mt-2 py-1'}>Quel identifiant utiliser ?</Link>
            </div>

            <div className="mb-6">
                <div className="relative">
                    <Text
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="current-password"
                        {...register('password')}
                        value={watch('password')}
                        required
                        label={'Mot de passe'}
                    />

                    <button
                        type="button"
                        onClick={togglePassword}
                        aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                        className="absolute right-1 top-1/2 -translate-y-1/2 border-none bg-transparent cursor-pointer p-2 leading-none"
                    >
                        <PictoVoirDetail style={{width: 22, height: 22, opacity: showPassword ? 0.4 : 1, transition: 'opacity 0.2s'}} />
                    </button>

                </div>

                <Link to={'/login/oubli-mot-de-passe'} className={'inline-block mt-2 py-1'}>Mot de passe oublié ?</Link>
            </div>

            <div className="grid gap-2">
                {
                    loading ? <SmallSpinner/> : <button type="submit" className={'inline-flex items-center justify-center gap-2 px-5 py-2.5 text-[0.875rem]/5 font-medium rounded-md transition-all ease-[ease] cursor-pointer no-underline disabled:opacity-50 disabled:pointer-events-none bg-primary text-primary-foreground border-none hover:bg-primary-hover'}>
                        Me connecter
                    </button>
                }
                {erreur && <p className="text-destructive" role="alert">Adresse email (exemple@domaine.fr) ou mot de passe invalide</p>}
            </div>

            <div className="flex items-center gap-3 my-6">
                <hr className="grow" />
                <span className="text-foreground">ou</span>
                <hr className="grow" />
            </div>

            <div className="grid">
                <Link to={'/login/oubli-mot-de-passe'} className={'gap-2 px-5 py-2.5 text-[0.875rem]/5 font-medium rounded-md transition-all ease-[ease] cursor-pointer no-underline disabled:opacity-50 disabled:pointer-events-none bg-transparent text-primary border border-primary hover:bg-transparent hover:text-foreground hover:border-foreground flex items-center justify-center'}>
                    <PictoMagicLink style={{width: 18, height: 18}} />
                    Recevoir un lien de connexion
                </Link>
            </div>

            <p className={'mt-6 text-center'}>Première visite ?{isPWA ? <br /> : ' '}<Link to={'/login/oubli-mot-de-passe'} className={'py-1'}>J'active mon Espace Assuré</Link></p>

            {/* Badges stores commentés en attendant la publication des apps. Décommenter quand prêt. */}
            {/* {!isPWA && HOST_MB === 43397 && <AppDownloadBanner variant="compact" />} */}
        </form>
    );
};

export default Connection;
